import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ActionButton } from '@/components/ui/ActionButton';
import { useToast } from '@/components/ui/Toast';
import { apiClient } from '@/lib/api';
import type { ApprovalRequest } from '@/lib/types';
import { deriveIdempotencyKey } from '../idempotency';
import { BucketShell } from './_BucketShell';
import { readDefaultFields, readStructuredFields } from './_payload';

function text(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

export function DateReviewView({ req }: { req: ApprovalRequest }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const defaults = readDefaultFields(req);
  const structured = readStructuredFields(req);
  const rawDate = structured['raw_date_text'] ?? defaults.raw_date_text;
  const parsedDate = structured['parsed_invoice_date'] ?? defaults.invoice_date;
  const confirmedDate = typeof parsedDate === 'string' ? parsedDate : null;
  const confirmMutation = useMutation({
    mutationFn: async () => {
      if (!confirmedDate) throw new Error('No parsed invoice date is available');
      const idem = deriveIdempotencyKey(req.id, req.updated_at, 'confirm-date');
      const response = await apiClient.post(
        `/automations/ap-purchase-invoice/${req.id}/reprocess`,
        {
          updated_at: req.updated_at,
          idempotency_key: idem,
          edits: {
            invoice_date: confirmedDate,
            confirm_invoice_date: true,
          },
        },
        { headers: { 'Idempotency-Key': idem } },
      );
      return response.data as { request?: ApprovalRequest };
    },
    onSuccess: (data) => {
      if (data.request) {
        queryClient.setQueryData(
          ['detail', 'ap-purchase-invoice', data.request.id],
          data.request,
        );
      }
      void queryClient.invalidateQueries({ queryKey: ['queue', 'ap-purchase-invoice'] });
      void queryClient.invalidateQueries({ queryKey: ['stats', 'ap-purchase-invoice'] });
      toast({
        variant: 'success',
        title: 'Invoice date confirmed',
        description: 'The audited date passed validation and is ready for approval.',
      });
    },
    onError: (error) => {
      toast({
        variant: 'error',
        title: 'Date confirmation blocked',
        description: error instanceof Error ? error.message : 'Date validation failed',
      });
    },
  });

  return (
    <BucketShell req={req}>
      <section className="rounded-lg border border-orange-300 bg-orange-50 p-16">
        <p className="text-xs font-semibold uppercase tracking-wide text-orange-800">
          Invoice date review
        </p>
        <p className="mt-4 text-sm text-neutral-800">
          Confirm the date against the scan, then use Save &amp; validate. The
          confirmed date is audited and must pass unchanged to Oracle.
        </p>
        <dl className="mt-12 grid gap-12 text-sm tablet:grid-cols-3">
          <Cell label="Printed date" value={text(rawDate)} />
          <Cell label="Parsed date" value={text(parsedDate)} />
          <Cell label="Detected format" value={text(structured['date_source_format'])} />
          <Cell label="Reason" value={text(structured['review_reason'])} />
          <Cell label="Fiscal year starts" value={text(structured['fiscal_year_start'])} />
          <Cell label="Fiscal year ends" value={text(structured['fiscal_year_end'])} />
        </dl>
        <div className="mt-12 flex justify-end">
          <ActionButton
            variant="primary"
            disabled={!confirmedDate || confirmMutation.isPending}
            loading={confirmMutation.isPending}
            onClick={() => confirmMutation.mutate()}
          >
            Confirm date &amp; validate
          </ActionButton>
        </div>
      </section>
    </BucketShell>
  );
}

function Cell({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-neutral-500">
        {label}
      </dt>
      <dd className="mt-2 font-mono text-neutral-900">{value}</dd>
    </div>
  );
}
